import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Users, FileDown, Upload, Download, Search, Calendar, Phone, User,
  Loader2, FileText, CheckCircle, AlertCircle, ArrowUpFromLine, X
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth } from '../context/AdminAuthContext';

const API_URL = '';
const T = { vert: '#1e4a1a', or: '#c4a76c', textPrimary: '#1a1a1a', textSecondary: '#6b7280', textMuted: '#9ca3af', border: '#e8e5e0' };
const CARD = { background: '#ffffff', border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' };

const TABS = [
  { id: 'prospects', label: 'Prospects', icon: Users },
  { id: 'brochure', label: 'Brochure PDF', icon: FileText },
];

const formatDate = (d) => {
  try {
    const date = new Date(d);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return d; }
};

const formatDateShort = (d) => {
  try {
    const date = new Date(d);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch (_) { return d; }
};

const isNew = (d) => {
  try {
    const date = new Date(d);
    const now = new Date();
    return (now - date) < 48 * 60 * 60 * 1000; // 48h
  } catch (_) { return false; }
};

const fmtSize = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const AdminProspectsPage = () => {
  const [activeTab, setActiveTab] = useState('prospects');

  return (
    <AdminLayout>
      <div className="max-w-6xl" data-testid="admin-prospects-page">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>
            Prospects & Brochures
          </h1>
          <p className="text-sm mt-1" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>
            Suivi des leads et gestion de la brochure
          </p>
        </div>

        <div className="flex gap-1 mb-8 p-1 rounded-xl" style={{ background: '#f0ede8' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`prospects-tab-${tab.id}`}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-medium transition-all"
              style={{ fontFamily: "'Montserrat', sans-serif", background: activeTab === tab.id ? '#ffffff' : 'transparent', color: activeTab === tab.id ? T.vert : T.textMuted, boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.06)' : 'none' }}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'prospects' && <ProspectsTab />}
        {activeTab === 'brochure' && <BrochureTab />}
      </div>
    </AdminLayout>
  );
};

// ===== PROSPECTS TAB =====
const ProspectsTab = () => {
  const { token } = useAdminAuth();
  const [prospects, setProspects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/prospects`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setProspects(data.prospects);
        setTotal(data.total);
      }
    } catch (err) { console.error('Failed to load prospects:', err); }
    setLoading(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/prospects/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'prospects_ogd.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) { console.error('Failed to export CSV:', err); }
    setExporting(false);
  };

  const filtered = prospects.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.phone?.includes(search)
  );

  return (
    <div className="space-y-6" data-testid="prospects-tab">
      {/* Header with stats and export */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-5">
          <div className="rounded-2xl px-5 py-3" style={CARD}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] mb-1" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>
              Total prospects
            </p>
            <p className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>{total}</p>
          </div>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: T.textMuted }} />
            <input
              type="text" placeholder="Rechercher un prospect..." value={search} onChange={e => setSearch(e.target.value)}
              data-testid="prospects-search"
              className="pl-11 pr-4 py-3 rounded-xl text-sm outline-none w-64"
              style={{ background: '#f5f3ef', border: `1px solid ${T.border}`, color: T.textPrimary, fontFamily: "'Montserrat', sans-serif" }}
            />
          </div>
        </div>
        <button
          onClick={handleExport} disabled={exporting}
          data-testid="export-csv-btn"
          className="flex items-center gap-2.5 px-6 py-3 rounded-full text-[13px] font-semibold transition-all duration-300"
          style={{ background: T.vert, color: '#ffffff', fontFamily: "'Montserrat', sans-serif", opacity: exporting ? 0.6 : 1 }}
          onMouseEnter={e => { if (!exporting) { e.currentTarget.style.background = '#163d14'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
          onMouseLeave={e => { e.currentTarget.style.background = T.vert; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          Exporter vers CSV
        </button>
      </div>

      {/* Prospects List */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.or }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 rounded-2xl" style={CARD}>
          <Users className="w-10 h-10 mx-auto mb-3" style={{ color: T.textMuted }} />
          <p style={{ color: T.textMuted, fontFamily: "'Montserrat', sans-serif" }}>
            {search ? 'Aucun résultat' : 'Aucun prospect pour le moment'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p, i) => (
            <div
              key={p.lead_id || i}
              className="rounded-2xl px-5 py-4 transition-all duration-200 hover:scale-[1.002]"
              style={CARD}
              data-testid={`prospect-row-${i}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(196,167,108,0.1)' }}>
                    <User className="w-4 h-4" style={{ color: T.or }} />
                  </div>

                  {/* Name + Phone */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5">
                      <p className="text-[14px] font-medium" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>
                        {p.name}
                      </p>
                      {isNew(p.created_at) && (
                        <span
                          className="px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] rounded-full flex-shrink-0"
                          style={{ background: 'rgba(196,167,108,0.08)', color: T.or, fontFamily: "'Montserrat', sans-serif" }}
                          data-testid="prospect-badge-new"
                        >
                          Nouveau
                        </span>
                      )}
                    </div>
                    <p className="text-[12px] flex items-center gap-1.5 mt-0.5" style={{ color: T.textMuted, fontFamily: "'Montserrat', sans-serif" }}>
                      <Phone className="w-3 h-3" />
                      {p.phone}
                    </p>
                  </div>
                </div>

                {/* Date */}
                <div className="flex items-center gap-1.5 flex-shrink-0" style={{ color: T.textMuted }}>
                  <Calendar className="w-3 h-3" />
                  <span className="text-[11px]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {formatDateShort(p.created_at)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== BROCHURE TAB =====
const BrochureTab = () => {
  const { token } = useAdminAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileRef = useRef(null);

  useEffect(() => { loadInfo(); }, []);

  const loadInfo = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/brochure/info`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setInfo(await res.json());
    } catch (err) { console.error('Failed to load brochure info:', err); }
    setLoading(false);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setMessage({ type: 'error', text: 'Seuls les fichiers PDF sont acceptés' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/brochure/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        setMessage({ type: 'success', text: 'Brochure mise à jour avec succès' });
        loadInfo();
      } else {
        const err = await res.json();
        setMessage({ type: 'error', text: err.detail || 'Erreur' });
      }
    } catch (err) {
      console.error('Failed to upload brochure:', err);
      setMessage({ type: 'error', text: 'Erreur de connexion' });
    }
    setUploading(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  }, [token]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.or }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="brochure-tab">
      {message.text && (
        <div className="p-4 rounded-xl text-sm flex items-center gap-2" style={{
          background: message.type === 'success' ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${message.type === 'success' ? 'rgba(74,222,128,0.2)' : 'rgba(239,68,68,0.2)'}`,
          color: message.type === 'success' ? '#4ade80' : '#f87171',
          fontFamily: "'Montserrat', sans-serif"
        }}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          {message.text}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-2xl p-5" style={CARD} data-testid="brochure-downloads-count">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(196,167,108,0.1)' }}>
              <Download className="w-4 h-4" style={{ color: T.or }} />
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>
              Téléchargements
            </span>
          </div>
          <p className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>{info?.download_count || 0}</p>
          <p className="text-[11px] mt-1" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>depuis le lancement</p>
        </div>

        <div className="rounded-2xl p-5" style={CARD} data-testid="brochure-leads-count">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(30,74,26,0.06)' }}>
              <Users className="w-4 h-4" style={{ color: T.vert }} />
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>
              Leads captés
            </span>
          </div>
          <p className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>{info?.total_leads || 0}</p>
          <p className="text-[11px] mt-1" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>prospects qualifiés</p>
        </div>

        <div className="rounded-2xl p-5" style={CARD} data-testid="brochure-file-info">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(30,74,26,0.06)' }}>
              <FileText className="w-4 h-4" style={{ color: T.vert }} />
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>
              Fichier actuel
            </span>
          </div>
          {info?.brochure ? (
            <>
              <p className="text-sm font-medium" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>{info.brochure.filename}</p>
              <p className="text-[11px] mt-1" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>
                {fmtSize(info.brochure.file_size)} &middot; {formatDateShort(info.brochure.uploaded_at)}
              </p>
            </>
          ) : (
            <p className="text-sm" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Aucun fichier</p>
          )}
        </div>
      </div>

      {/* Drag & Drop Upload Zone */}
      <div
        className="rounded-2xl transition-all duration-300 cursor-pointer"
        style={{
          ...CARD,
          borderStyle: 'dashed',
          borderWidth: '2px',
          borderColor: dragActive ? T.or : T.border,
          background: dragActive ? 'rgba(196,167,108,0.06)' : '#faf9f6',
        }}
        data-testid="brochure-dropzone"
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
      >
        <input type="file" ref={fileRef} onChange={handleFileChange} accept=".pdf" className="hidden" />
        <div className="flex flex-col items-center justify-center py-14 px-6">
          {uploading ? (
            <>
              <Loader2 className="w-10 h-10 animate-spin mb-4" style={{ color: T.or }} />
              <p className="text-sm font-medium text-sm" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>
                Envoi en cours...
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'rgba(196,167,108,0.1)' }}>
                <ArrowUpFromLine className="w-7 h-7" style={{ color: T.or }} />
              </div>
              <p className="text-base font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>
                Glissez votre PDF ici
              </p>
              <p className="text-[13px] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>
                ou cliquez pour sélectionner un fichier
              </p>
              <div
                className="px-6 py-2.5 rounded-full text-[12px] font-semibold transition-all"
                style={{ background: 'rgba(196,167,108,0.08)', color: T.or, fontFamily: "'Montserrat', sans-serif" }}
              >
                Parcourir les fichiers
              </div>
            </>
          )}
        </div>
      </div>

      {/* Current brochure details */}
      {info?.brochure && (
        <div className="rounded-2xl p-6" style={CARD} data-testid="brochure-current-details">
          <h3 className="text-[13px] font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>
            <FileText className="w-4 h-4" style={{ color: T.or }} />
            Brochure en ligne
          </h3>
          <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: '#f5f3ef' }}>
            <div className="w-12 h-14 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239,68,68,0.1)' }}>
              <FileText className="w-6 h-6" style={{ color: '#f87171' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>{info.brochure.filename}</p>
              <p className="text-[11px] mt-1" style={{ color: T.textMuted, fontFamily: "'Montserrat', sans-serif" }}>
                {fmtSize(info.brochure.file_size)} &middot; Mis à jour le {formatDate(info.brochure.uploaded_at)}
                {info.brochure.uploaded_by && <> par {info.brochure.uploaded_by}</>}
              </p>
            </div>
            <a
              href="/brochure-ogd.pdf" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] font-semibold transition-all"
              style={{ background: 'rgba(30,74,26,0.06)', color: T.textSecondary, fontFamily: "'Montserrat', sans-serif" }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(30,74,26,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(30,74,26,0.06)'; }}
              data-testid="preview-brochure-btn"
            >
              <Download className="w-3.5 h-3.5" /> Aperçu
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProspectsPage;
